import { Injectable, inject } from '@angular/core';
import { type components, NarratorVoice } from '@storees/api-types';
import { DexieNarratorsService } from './dexie/dexie-narrators.service';
import { firstValueFrom } from 'rxjs';
import { AuthService } from './auth-service';
import { VoiceCloneService } from './voice-clone.service';

type Narrator = components['schemas']['Narrator'];

@Injectable({ providedIn: 'root' })
export class NarratorService {
  private authService = inject(AuthService);
  private dexieNarratorsService = inject(DexieNarratorsService);
  private voiceCloneService = inject(VoiceCloneService);

  _narratorVoices: Narrator[] = [];
  get narratorVoices(): Narrator[] {
    return this._narratorVoices;
  }

  async getNarrators(): Promise<Narrator[]> {
    try {
      const jwtToken = await this.authService.getSessionJwtToken();
      await this.dexieNarratorsService.syncNarrators(jwtToken);
      const result = await firstValueFrom(this.dexieNarratorsService.getNarrators());
      const base = Array.isArray(result) ? result : [];
      const filtered = base.filter(
        (n) =>
          n.voice !== NarratorVoice.voice_clone ||
          VoiceCloneService.isFinalizedVoiceCloneForPicker(n)
      );
      this._narratorVoices = await this.voiceCloneService.mergeVoiceClonePlaceholdersIntoNarrators(
        filtered
      );
      return this._narratorVoices;
    } catch (error: unknown) {
      // Enhanced error logging for troubleshooting
      if (error && typeof error === 'object' && 'status' in error) {
        const httpError = error as { status: number; statusText?: string; message?: string; error?: unknown; url?: string };
        console.error('Error fetching narrators - HTTP Error:', JSON.stringify({
          status: httpError.status,
          statusText: httpError.statusText,
          message: httpError.message,
          error: httpError.error,
          url: httpError.url
        }, null, 2));
      } else if (error instanceof Error) {
        console.error('Error fetching narrators:', error.message, error.stack);
      } else {
        console.error('Error fetching narrators:', JSON.stringify(error));
      }
      this._narratorVoices = []; // Reset to empty array on error
      return this._narratorVoices;
    }
  }
}
